import { Grid } from "@mui/material";
import { Icon } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext } from "react";
import { ListingComponentContext } from "../common/listingComponents/ListingComponentContext";
import AvailableSurveyCard from "./AvailableSurveyCard";
import AvailableSurveysListView from "./AvailableSurveysListView";
import { mapSurveyToRow } from "./surveyRowMapper";

// Edit opens the create/edit drawer (card 26405967) via onEdit. Delete opens a
// confirmation modal via onDelete, which calls the BFF deleteSurveyDefinition
// mutation (ADO 26406046). Each action is disabled only if its handler is absent.
const buildActionButtons = (row, onEdit, onDelete) => [
  { icon: "edit", onClick: () => onEdit?.(row), disabled: !onEdit },
  { icon: "delete", onClick: () => onDelete?.(row), disabled: !onDelete },
];

const AvailableSurveysListComponent = ({ nameSearch, onEdit, onDelete }) => {
  const { viewType, viewableRecords, filteredRecords } = useContext(
    ListingComponentContext
  );

  const search = (nameSearch || "").trim().toLowerCase();

  // When a name search is active, filter the full set (filteredRecords.items) client-side,
  // because the BFF filter input has no name field. This intentionally bypasses the harness
  // pager and assumes filteredRecords.items holds the whole result set — true while the survey
  // BFF is not server-paginated (list-all). Revisit if server pagination is added.
  const fullSet = filteredRecords?.items || [];
  const sourceRecords = search ? fullSet : viewableRecords || [];

  const rows = sourceRecords
    .map(mapSurveyToRow)
    .filter((row) => !search || row.surveyName.toLowerCase().includes(search));

  // The BFF returns a top-level `errors` array on SurveyDefinitionsResponse. Surface it as a
  // distinct state so a backend failure isn't mistaken for an empty list.
  const errors = filteredRecords?.errors || [];
  if (errors.length > 0) {
    return (
      <div className="survey-empty survey-empty--error">
        <Icon icon="error" size="medium" alt="" />
        <p className="survey-empty__message">
          Something Went Wrong Loading Surveys. Please Try Again.
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="survey-empty">
        <Icon icon="info" size="medium" alt="" />
        <p className="survey-empty__message">No Surveys Found</p>
      </div>
    );
  }

  if (viewType === "grid") {
    return (
      <Grid container spacing={2}>
        {rows.map((row, index) => (
          <Grid
            key={`${row.surveyId || row.surveyName}-${index}`}
            item
            xs={12}
            md={6}
            lg={4}
          >
            <AvailableSurveyCard
              row={row}
              actionButtons={buildActionButtons(row, onEdit, onDelete)}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <AvailableSurveysListView
      rows={rows}
      generateActionButtons={(row) => buildActionButtons(row, onEdit, onDelete)}
    />
  );
};

AvailableSurveysListComponent.propTypes = {
  nameSearch: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default AvailableSurveysListComponent;
